import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AlertService} from "../services";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';

import {first} from "rxjs/operators";
import {VoucherTemplate} from "../models/voucherTemplate";
import {VoucherTemplateService} from "../services/voucherTemplate.service";
import { locale as polish } from './i18n/pl';
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";
import {MatDialog} from "@angular/material/dialog";
import {MarketingAction} from "../models/marketingAction";
import {DeleteMarketingActionDialogComponent} from "../marketingAction/dialog/deleteMarketingActionDialog.component";
import {FormGroup} from "@angular/forms";
import {DeleteVoucherTemplateDialogComponent} from "./dialog/deleteVoucherTemplateDialog.component";
import {Items} from "../models/items";
import {Page} from "../models/page";
import {DatatableComponent} from "@swimlane/ngx-datatable";
import {VoucherTemplateGroup} from "../models/voucherTemplateGroup";

@Component({
    templateUrl: './voucherTemplate.component.html',
    styleUrls: ['./voucherTemplate.component.scss']
})
export class VoucherTemplateComponent implements OnDestroy, OnInit {
    rows: VoucherTemplate[];
    allRows: VoucherTemplate[] = [];
    loadingIndicator: boolean;
    reorderable: boolean;
    selected = [];
    deleteDialogRef: any;
    filterValue: string;
    page = new Page();
    voucherGroups: VoucherTemplateGroup[] = [];
    selectedVoucherGroupId: string | null = null;
    filterRecommended: boolean = false;
    filterTheLatest: boolean = false;
    @ViewChild(DatatableComponent) table: DatatableComponent;

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        private alertService: AlertService,
        private _fuseSidebarService: FuseSidebarService,
        private voucherTemplateService: VoucherTemplateService,
        private translationLoader: FuseTranslationLoaderService,
        public _matDialog: MatDialog,
    ) {
        this.translationLoader.loadTranslations(polish);

        // Set the defaults
        this.loadingIndicator = true;
        this.reorderable = true;

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    ngOnInit() {
        // Load saved filters from localStorage
        const savedGroupId = localStorage.getItem('voucherTemplateSelectedGroup');
        if (savedGroupId && savedGroupId !== 'null') {
            this.selectedVoucherGroupId = savedGroupId;
        }

        const savedRecommended = localStorage.getItem('voucherTemplateFilterRecommended');
        this.filterRecommended = savedRecommended === 'true';

        const savedTheLatest = localStorage.getItem('voucherTemplateFilterTheLatest');
        this.filterTheLatest = savedTheLatest === 'true';

        this.voucherTemplateService.getAll().pipe(first())
            .subscribe( (res: VoucherTemplate[]) => {
                    this.allRows = res;
                    this.extractVoucherGroups(res);

                    // Apply saved filters
                    this.applyAllFilters();

                    this.loadingIndicator = false;
                },
                error => {
                    this.alertService.error(error);
                })
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }


    /**
     * Toggle the sidebar
     *
     * @param name
     */
    toggleSidebar(name): void
    {
        this._fuseSidebarService.getSidebar(name).toggleOpen();
    }

    showConfirmVoucherTemplateDialog(voucher: VoucherTemplate) {
        this.deleteDialogRef = this._matDialog.open(DeleteVoucherTemplateDialogComponent, {
            panelClass: 'delete-voucher-template-dialog',
            data      : {
                action : 'show',
                voucher: voucher,
            },
            width: '350px',
        });

        this.deleteDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                this.voucherTemplateService.delete(voucher.id).pipe(first())
                    .subscribe(res => {
                        window.location.reload();
                    });

                this.deleteDialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }
    public unsubscribe(): void {
        this._unsubscribeAll.next();
    }

    updateFilter(event) {
        this.unsubscribe();
        this.filterValue = event.target.value.toLowerCase();

        if (this.filterValue.length < 3) {
            return;
        }

        this.loadingIndicator = true;

        this.voucherTemplateService.getAllBy(this.filterValue).pipe(takeUntil(this._unsubscribeAll))
            .subscribe( (res: VoucherTemplate[]) => {
                    this.allRows = res;
                    this.rows = res;
                    this.extractVoucherGroups(res);
                    this.selectedVoucherGroupId = null;
                    this.loadingIndicator = false;
                    this.page.totalElements = res.length;
                },
                error => {
                    this.alertService.error(error);
                })

        this.table.offset = 0;
    }

    extractVoucherGroups(voucherTemplates: VoucherTemplate[]): void {
        const groupsMap = new Map<string, VoucherTemplateGroup>();

        voucherTemplates.forEach(voucher => {
            if (voucher.group && voucher.group.id) {
                groupsMap.set(voucher.group.id, voucher.group);
            }
        });

        this.voucherGroups = Array.from(groupsMap.values());
    }

    filterByVoucherGroup(groupId: string | null): void {
        this.selectedVoucherGroupId = groupId;

        // Save to localStorage
        if (groupId === null) {
            localStorage.removeItem('voucherTemplateSelectedGroup');
            // Reset other filters when clicking "All Vouchers"
            this.filterRecommended = false;
            this.filterTheLatest = false;
            localStorage.removeItem('voucherTemplateFilterRecommended');
            localStorage.removeItem('voucherTemplateFilterTheLatest');
        } else {
            localStorage.setItem('voucherTemplateSelectedGroup', groupId);
        }

        this.applyAllFilters();
    }

    toggleRecommended(): void {
        this.filterRecommended = !this.filterRecommended;
        localStorage.setItem('voucherTemplateFilterRecommended', this.filterRecommended.toString());
        this.applyAllFilters();
    }

    toggleTheLatest(): void {
        this.filterTheLatest = !this.filterTheLatest;
        localStorage.setItem('voucherTemplateFilterTheLatest', this.filterTheLatest.toString());
        this.applyAllFilters();
    }

    applyAllFilters(): void {
        let filteredRows = [...this.allRows];

        // Filter by group
        if (this.selectedVoucherGroupId) {
            filteredRows = filteredRows.filter(voucher => voucher.group && voucher.group.id === this.selectedVoucherGroupId);
        }

        // Filter by recommended
        if (this.filterRecommended) {
            filteredRows = filteredRows.filter(voucher => voucher.recommended === true);
        }

        // Filter by theLatest
        if (this.filterTheLatest) {
            filteredRows = filteredRows.filter(voucher => voucher.theLatest === true);
        }

        this.rows = filteredRows;
        this.page.totalElements = this.rows.length;

        if (this.table) {
            this.table.offset = 0;
        }
    }
}
